import React, { useEffect, useMemo, useState, useRef, RefObject } from 'react';
import { useNavigate } from 'react-router-dom';

import CarWhite from '../../../img/car-white.svg'
import SearchIcon from '../../../img/search-icon.svg';

// at-65 (BUG-V35): a Home deixou de consultar `atividade-carro` e `status-history` por
// veículo — a régua do kanban vem de `GET /cars/kanban-regua`, uma chamada para o lote
// inteiro. O `getCarStatusHistory` continua importado porque o modal de detalhes de um
// veículo (um por vez) ainda o usa.
import { getCarStatusHistory, getKanbanRegua, type KanbanReguaItem } from '../../../api/carsApi';
import {
  useHomeCarsQuery,
  useHomeNonEntregaSummaryQuery,
} from '../../../infrastructure/react-query/hooks/useHomeQueries';


import { notify } from '../../../shared/feedback/notify';
import { useEsteiras } from '../../../hooks/useEsteiras';
import { derivarReguaDoVeiculo, montarBaseDaRegua } from '../regua.util';
import { LIMIAR_PARADO_HORAS, msSemMovimentacao } from '../home.util';
import TelaoOficina from '../../telao/TelaoOficina';
import { SECOES_DE_AJUDA_DO_TELAO } from '../../telao/ajudaDoTelao';
import { TELAO } from '../../telao/routes';
import Relogio from '../../telao/Relogio';
import LogoSoac from '../../../components/logo-soac/LogoSoac';
import { useTemaResolvido } from '../../../shared/tema/tema';

import CarHomeDetailsModal from '../../../components/modal/car-home-details/CarHomeDetailsModal';

import FullScreenLoader from '../../../components/loading/FullScreenLoader';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import '../../../components/como-funciona/help-circle-btn.css';
// at-130: a régua de esteiras saiu do home.css (o modal também a usa). Importada ANTES do
// home.css para preservar a ordem da cascata: os overrides do fim do home.css continuam vencendo.
import '../../../shared/ui/regua-esteiras.css';
import '../../recepcao/pages/Recepcao.css'; // at-130: abas (.recepcao-tabs), molde de "Em atendimento / Entregues"
import './home.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface HomeProps {
  sidebarRef?: RefObject<HTMLDivElement>;
}

type SummaryIconName =
  | 'garage'
  | 'warning'
  | 'lanes'
  | 'trendUp'
  | 'check'
  | 'clipboard'
  | 'wrench'
  | 'paint'
  | 'parts'
  | 'delivery'
  | 'spark';

const SummaryIcon = ({ icon }: { icon: SummaryIconName }) => {
  switch (icon) {
    case 'garage':
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M3 11.5L12 4L21 11.5V20H3V11.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M8.5 20V13H15.5V20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'warning':
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 4L21 20H3L12 4Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          <path d="M12 9V13.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="12" cy="17" r="1" fill="currentColor" />
        </svg>
      );
    case 'lanes':
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M5 5V19" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M12 5V10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M12 14V19" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M19 5V19" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'trendUp':
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M5 16L10 11L14 15L20 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M15 9H20V14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'check':
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
          <path d="M8 12.5L10.8 15.3L16.5 9.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'clipboard':
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="6" y="5" width="12" height="15" rx="2" stroke="currentColor" strokeWidth="1.8" />
          <path d="M9 5.5H15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M9 10H15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'wrench':
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M14.5 6.5C15.6 3.9 18.4 3 20.5 4.2L17 7.7L18.3 11L21.8 7.5C23 9.6 22.1 12.4 19.5 13.5L11 22L8 19L14.5 12.5C13.2 10.9 13 8.6 14.5 6.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        </svg>
      );
    case 'paint':
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M7 7L17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M14 4L20 10L10 20H4V14L14 4Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
      );
    case 'parts':
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 4L14.4 8.6L20 9.4L16 13.1L16.9 18.6L12 16.1L7.1 18.6L8 13.1L4 9.4L9.6 8.6L12 4Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        </svg>
      );
    case 'delivery':
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M4 7H14V15H4V7Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          <path d="M14 10H18L20 12.5V15H14V10Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          <circle cx="8" cy="17" r="1.8" fill="currentColor" />
          <circle cx="17" cy="17" r="1.8" fill="currentColor" />
        </svg>
      );
    case 'spark':
    default:
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 3L13.8 8.2L19 10L13.8 11.8L12 17L10.2 11.8L5 10L10.2 8.2L12 3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
      );
  }
};

const safeDate = (value?: string | null) => {
  if (!value) return null;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * No tema escuro, a cor cadastrada da esteira (pastel, pensada para o claro) vira só um
 * tom sobre a superfície escura: usada cheia, a coluna era uma faixa clara atrás de
 * cartões escuros. O texto passa a ser o do tema.
 */
function corDaEsteiraNoTema(cor: string | null | undefined, escuro: boolean, intensidade: number) {
  if (!escuro || !cor) return { fundo: cor || undefined, texto: getContrastColor(cor) };
  return {
    fundo: `color-mix(in srgb, ${cor} ${intensidade}%, var(--tema-superficie))`,
    texto: 'var(--tema-texto)',
  };
}

function getContrastColor(hex?: string | null, fallback = '#1f2937') {
  if (!hex) return fallback;
  const normalized = hex.replace('#', '');
  if (normalized.length !== 3 && normalized.length !== 6) return fallback;
  const full = normalized.length === 3
    ? normalized.split('').map((char) => `${char}${char}`).join('')
    : normalized;

  const red = parseInt(full.substring(0, 2), 16);
  const green = parseInt(full.substring(2, 4), 16);
  const blue = parseInt(full.substring(4, 6), 16);
  const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;

  return luminance > 0.66 ? '#1f2937' : '#FFFFFF';
}

const STATUS_LANE_SHORT_NAMES: Record<string, string> = {
  AGUARDANDO: 'Aguard.',
  AGUARDANDO_PROXIMA_ETAPA: 'Ag. Próx.',
  DESMONTAGEM: 'Desmont.',
  ORCAMENTO: 'Orçam.',
  AGUARDANDO_PECAS: 'Ag. Peças',
  FUNILARIA: 'Funil.',
  PREPARACAO_PINTURA: 'Prep. Pint.',
  PINTURA: 'Pintura',
  MECANICA: 'Mecân.',
  ELETRICA: 'Elétr.',
  AGUARDANDO_COMPLEMENTOS: 'Ag. Compl.',
  MONTAGEM: 'Mont.',
  SERVICO_EXTERNO: 'Serv. Ext.',
  POLIMENTO: 'Polim.',
  HIGIENIZACAO: 'Higien.',
  CORRECAO: 'Correc.',
  AGUARDANDO_ENTREGA: 'Ag. Entr.',
};

type AbaDaHome = 'quadro' | 'telao';

const HomePage: React.FC<HomeProps> = ({ sidebarRef }) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  // at-130: duas abas — o kanban de sempre ("Quadro de esteiras") e o Telão por fases. O kanban
  // NÃO foi removido (decisão do Victor, 23/10/2026).
  // 🔴 A Home SEMPRE abre no Quadro de esteiras (Victor, 23/10/2026 — repetido três vezes). A aba
  // NÃO é lembrada entre visitas: lembrar fazia quem tinha olhado o Telão voltar a cair nele.
  const [aba, setAba] = useState<AbaDaHome>('quadro');
  const trocarAba = (proxima: AbaDaHome) => setAba(proxima);
  const [kanbanStatusHistory, setKanbanStatusHistory] = useState<{ [carId: string]: any[] }>({});
  const [statusIconsHistory, setStatusIconsHistory] = useState<{ [carId: string]: any[] }>({});
  const [plannedStatusesByCarId, setPlannedStatusesByCarId] = useState<Record<string, string[]>>({});
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [appliedSearchQuery, setAppliedSearchQuery] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('plate');
  const [selectedCarId, setSelectedCarId] = useState<string | null>(null);
  const [carHomeDetailsModalOpen, setCarHomeDetailsModalOpenOpen] = useState(false);
  // at-46 (pedido de 02/08/2026): modal com a lista dos veículos parados (card do resumo).
  const [paradosModalOpen, setParadosModalOpen] = useState(false);
  const [paradosPage, setParadosPage] = useState(1);
  const PARADOS_PAGE_SIZE = 10;
  const [isStatusSidebarOpen] = useState<boolean>(true);
  const etapaTextoRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const kanbanRef = useRef<HTMLDivElement>(null);

  const scrollToLane = (esteiraId: string) => {
    if (!kanbanRef.current) return;
    const laneElement = kanbanRef.current.querySelector(`[data-lane-id="${esteiraId}"]`) as HTMLElement;
    if (laneElement) {
      const container = kanbanRef.current;
      const targetScrollLeft = laneElement.offsetLeft - container.offsetLeft - (container.clientWidth / 2) + (laneElement.clientWidth / 2);
      container.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth',
      });
    }
  };
  const [isKanbanDragging, setIsKanbanDragging] = useState(false);
  const kanbanDragRef = useRef({
    isDown: false,
    pointerId: null as number | null,
    startX: 0,
    startScrollLeft: 0,
    didDrag: false,
    lastDragAt: 0,
    lastX: 0,
    lastTime: 0,
    velocity: 0,
    animationId: null as number | null,
  });

  const { esteirasAtivas, labelDe, isLoading: loadingEsteiras } = useEsteiras();
  const temaEscuro = useTemaResolvido() === 'escuro';
  const kanbanEsteiras = useMemo(() => {
    return esteirasAtivas.filter((e) => e.chave !== 'ENTREGA');
  }, [esteirasAtivas]);

  const homeCarsQuery = useHomeCarsQuery();
  const nonEntregaSummaryQuery = useHomeNonEntregaSummaryQuery();

  const [isDetailsLoading, setIsDetailsLoading] = useState<boolean>(false);

  const loading =
    homeCarsQuery.isLoading || nonEntregaSummaryQuery.isLoading || isDetailsLoading || loadingEsteiras;

  const allCars = useMemo(() => ((homeCarsQuery.data || []) as any[]), [homeCarsQuery.data]);

  const cars = useMemo(() => {
    const normalizedQuery = appliedSearchQuery.trim().toLowerCase();

    if (!normalizedQuery) return allCars;

    return allCars.filter((car) => {
      if (searchFilter === 'plate') {
        return String(car.plate || '').toLowerCase().includes(normalizedQuery);
      }
      if (searchFilter === 'chassis') {
        return String(car.chassis || '').toLowerCase().includes(normalizedQuery);
      }
      if (searchFilter === 'model') {
        return String(car.model || '').toLowerCase().includes(normalizedQuery);
      }
      if (searchFilter === 'eventNumber') {
        return String(car.eventNumber || '').includes(appliedSearchQuery.trim());
      }
      return true;
    });
  }, [allCars, appliedSearchQuery, searchFilter]);

  const nonEntregaTotal = nonEntregaSummaryQuery.data?.total || 0;

  // Veículos em esteira marcada para não contar (Indenização Integral e Negativa, por padrão)
  // saem só do card "Total na oficina"; continuam no quadro e no título do painel.
  const foraDoTotal = useMemo(() => {
    const ids = new Set(esteirasAtivas.filter((e) => e.contaNoTotal === false).map((e) => e.id));
    return allCars.filter((car) => ids.has(car.esteiraId)).length;
  }, [allCars, esteirasAtivas]);
  const totalNaOficina = Math.max(0, nonEntregaTotal - foraDoTotal);

  const carsByLane = useMemo(() => {
    const laneMap: Record<string, any[]> = {};

    kanbanEsteiras.forEach((est) => {
      // at-96: o `sortCarsByUrgency` saiu. Ele priorizava tipo/esteira RETORNO ou REPARO e,
      // medido em produção, NUNCA ordenou nada (0 sessões desses tipos, e não existe esteira
      // com esses nomes) — a ordenação era estável por índice, então a ordem visível não muda.
      laneMap[est.id] = cars.filter((car) => car.esteiraId === est.id);
    });

    return laneMap;
  }, [cars, kanbanEsteiras]);

  const allCarsByLane = useMemo(() => {
    const laneMap: Record<string, any[]> = {};

    kanbanEsteiras.forEach((est) => {
      laneMap[est.id] = allCars.filter((car) => car.esteiraId === est.id);
    });

    return laneMap;
  }, [allCars, kanbanEsteiras]);

  const operationalSummary = useMemo(() => {
    const now = new Date();
    const last30Days = new Date(now);
    last30Days.setDate(last30Days.getDate() - 30);

    // Lista completa (não só a contagem): o card "Veículos parados" abre um modal com os
    // veículos, ordenados do parado há mais tempo para o mais recente.
    // at-130: a conta vive em `home.util.ts` — o selo de "parado" da placa do Telão usa a mesma.
    const stoppedCars = allCars
      .map((car) => {
        const stoppedMs = msSemMovimentacao(car, now);
        if (stoppedMs === null || stoppedMs < LIMIAR_PARADO_HORAS * 60 * 60 * 1000) return null;
        return { car, stoppedMs };
      })
      .filter((item): item is { car: any; stoppedMs: number } => item !== null)
      .sort((a, b) => b.stoppedMs - a.stoppedMs);

    const stoppedCount = stoppedCars.length;

    const ingressCount = allCars.filter((car) => {
      const entryDate = safeDate(car.entryDate) || safeDate(car.createdAt);
      return Boolean(entryDate && entryDate >= last30Days);
    }).length;

    const deliveredCount = allCars.filter((car) => {
      if (String(car.status || '').toUpperCase() !== 'ENTREGA') return false;

      const deliveredDate = safeDate(car.statusUpdatedAt) || safeDate(car.updatedAt) || safeDate(car.createdAt);
      return Boolean(deliveredDate && deliveredDate >= last30Days);
    }).length;

    const laneItems = kanbanEsteiras.map((est) => ({
      status: est.id,
      label: est.nome,
      cor: est.cor,
      count: allCarsByLane[est.id]?.length || 0,
    })).filter((item) => item.count > 0);

    const laneTotal = laneItems.reduce((total, item) => total + item.count, 0);

    return {
      stoppedCount,
      stoppedCars,
      ingressCount,
      deliveredCount,
      laneItems,
      laneTotal,
    };
  }, [allCars, allCarsByLane, kanbanEsteiras]);

  const helpSections: ComoFuncionaSecao[] = [
    {
      titulo: 'O que esta tela mostra',
      conteudo: (
        <p>
          O Painel Geral é a fotografia da oficina inteira: todos os veículos em atendimento (os entregues não
          aparecem), organizados em um quadro com <strong>uma coluna por esteira</strong>. O topo resume a operação —{' '}
          <strong>Total na oficina</strong>, <strong>Veículos parados</strong> e a <strong>Distribuição por
          esteira</strong>. A tela é somente consulta: nenhuma ação operacional é feita por aqui, e ela é a mesma
          para todos os perfis.
        </p>
      ),
    },
    {
      titulo: 'Veículos parados',
      conteudo: (
        <p>
          O cartão <strong>Veículos parados</strong> conta os veículos <strong>sem movimentação de esteira há mais
          de 48 horas</strong>. Clique nele para ver a lista, do parado há mais tempo para o mais recente — o tempo
          conta desde a última mudança de esteira do veículo, não desde a entrada na oficina. Clicar na placa abre
          os detalhes do atendimento.
        </p>
      ),
    },
    {
      titulo: 'Navegar pelo quadro',
      conteudo: (
        <p>
          O quadro rola para os lados — <strong>arraste</strong> com o mouse ou o dedo. Cada pílula do cartão{' '}
          <strong>Distribuição por esteira</strong> leva direto à coluna correspondente. Clicar em um veículo abre a
          consulta do atendimento: dados, observações, imagens e o histórico de visitas anteriores da mesma placa.
        </p>
      ),
    },
    {
      titulo: 'As cores da régua de esteiras',
      conteudo: (
        <>
          <p>
            Dentro de cada cartão de veículo, a régua <strong>Esteiras</strong> mostra o caminho planejado e
            percorrido. As cores seguem a legenda <strong>Situação das esteiras</strong> no topo do quadro:
          </p>
          <p>
            <strong>Atual</strong> — a etapa em que o veículo está agora. <strong>Realizada</strong> — etapa já
            concluída. <strong>Serviço inacabado</strong> — etapa em que o serviço começou e foi interrompido sem
            conclusão; o veículo pode voltar para retomá-la. <strong>Prevista</strong> — etapa planejada e ainda não
            realizada. <strong>Etapa do sistema</strong> — etapas de controle (espera, definição de próxima etapa,
            entrega), que não são serviço. <strong>Fora do planejamento</strong> — etapa que existe na oficina mas
            não está no plano deste veículo.
          </p>
          <p>
            <strong>Paralela atual</strong> (turquesa, borda tracejada) é diferente das demais: não é uma fase do
            caminho do veículo, e sim um <strong>serviço acontecendo agora</strong> numa esteira que não é a etapa
            dele. O veículo continua onde está — o prazo da etapa não reinicia e o cartão não muda de coluna —, e
            outro setor trabalha nele ao mesmo tempo. Por isso um mesmo cartão pode ter um chip laranja e um
            turquesa.
          </p>
          <p>
            Quando o serviço de uma esteira aberta em paralelo é <strong>interrompido sem conclusão</strong>, o chip
            aparece como <strong>Serviço inacabado</strong>: a esteira continua aberta, mas a pendência é o que
            precisa ser visto primeiro. Clique no selo ao lado da placa para ver e controlar as esteiras em paralelo
            do veículo.
          </p>
          <p>
            Ao clicar na placa, o modal <strong>Informações do atendimento</strong> mostra a mesma régua com a
            legenda das cores, incluindo as etapas <strong>Fora do planejamento</strong>, que no cartão aparecem
            apagadas.
          </p>
        </>
      ),
    },
    {
      titulo: 'Selos nos cartões',
      conteudo: (
        <p>
          Na coluna de espera, os veículos exibem a situação da liberação administrativa: <strong>Liberado</strong>{' '}
          (liberação total) ou <strong>Liberação parcial</strong> (apenas algumas esteiras liberadas) — sem
          liberação, o veículo não recebe colaborador nem inicia serviço na esteira. <strong>Retorno para ag.</strong>{' '}
          marca veículo que voltou para a espera. Nos demais casos, o selo mostra as tags preenchidas do veículo (por
          exemplo, o motivo do aguardo).
        </p>
      ),
    },
    {
      titulo: 'Busca',
      conteudo: (
        <p>
          Escolha o campo em <strong>Filtro por</strong> (Placa, Chassi, Modelo ou Número do Evento), digite o{' '}
          <strong>Termo de Busca</strong> e confirme com Enter ou a lupa — o quadro passa a mostrar só os veículos
          correspondentes. As contagens das pílulas de distribuição continuam refletindo a oficina inteira.
        </p>
      ),
    },
    {
      titulo: 'As duas abas: Quadro de esteiras e Telão',
      conteudo: (
        <p>
          <strong>Quadro de esteiras</strong> é o painel de sempre, com uma coluna por esteira. <strong>Telão</strong>{' '}
          mostra os mesmos veículos de forma compacta, agrupados em cinco fases e desenhados como placas, com as
          páginas trocando sozinhas — feito para ficar aberto na TV da oficina. A busca e o resumo do topo valem só
          para o Quadro. As seções abaixo explicam o Telão.
        </p>
      ),
    },
    ...SECOES_DE_AJUDA_DO_TELAO.map((s) => ({ ...s, titulo: `Telão — ${s.titulo}` })),
  ];

  const summaryCards = [
    {
      variant: 'neutral',
      label: 'Total na oficina',
      value: totalNaOficina,
      meta: 'Veículos em processo',
      icon: 'garage' as SummaryIconName,
    },
    {
      variant: 'warning',
      label: 'Veículos parados',
      value: operationalSummary.stoppedCount,
      meta: 'Sem movimentação acima de 48h',
      icon: 'warning' as SummaryIconName,
    },
  ];

  /**
   * Carrega a régua de esteiras dos cards do kanban (at-65, BUG-V35).
   *
   * Antes eram **duas funções e 2 requisições por veículo** (`atividade-carro` e
   * `status-history`): 40 cards = ~80 chamadas na primeira carga, refeitas a cada troca de
   * página. Agora é **uma** chamada para o lote todo (`GET /cars/kanban-regua`), que
   * devolve planejadas, aprovadas e a última aprovada por carro.
   *
   * O cache por carro foi mantido: só entra no pedido quem ainda não tem régua montada —
   * é o que evita refazer a consulta a cada preenchimento de estado (at-43).
   */
  const loadKanbanRegua = async (carIds: string[]) => {
    if (kanbanEsteiras.length === 0) return;

    // Array vazio conta como "faltando" para se autocorrigir caso alguma legenda tenha
    // sido gravada antes das esteiras carregarem.
    const idsToLoad = (carIds || []).filter(
      (id) =>
        id &&
        (plannedStatusesByCarId[id] === undefined ||
          kanbanStatusHistory[id] === undefined ||
          kanbanStatusHistory[id].length === 0)
    );
    if (idsToLoad.length === 0) return;

    let itens: KanbanReguaItem[];
    try {
      itens = await getKanbanRegua(idsToLoad);
    } catch (error) {
      // Régua vazia é indistinguível de "veículo que não passou por esteira nenhuma" —
      // e o card fica sem a informação de etapa sem dizer por quê (regra da at-60).
      notify.errorFrom(
        error,
        'Não foi possível carregar a situação das esteiras dos veículos. Recarregue a página; se persistir, acione a TI.',
        { toastId: 'home-kanban-regua' }
      );
      return;
    }

    const nextPlanned: Record<string, string[]> = {};
    const nextKanban: Record<string, any[]> = {};
    const nextIcons: Record<string, any[]> = {};

    itens.forEach((item) => {
      nextPlanned[item.carId] = item.planejadas ?? [];

      nextKanban[item.carId] = kanbanEsteiras.map((est) => ({
        name: est.chave,
        approved: est.id === item.ultimaAprovada,
      }));

      // 🔴 at-99: `realizadas`, não `aprovadas` — a regra (e o porquê) vive em `regua.util.ts`.
      nextIcons[item.carId] = montarBaseDaRegua(kanbanEsteiras, item);
    });

    if (Object.keys(nextPlanned).length > 0) {
      setPlannedStatusesByCarId((prev) => ({ ...prev, ...nextPlanned }));
    }

    if (Object.keys(nextKanban).length > 0) {
      setKanbanStatusHistory((prevHistory) => ({ ...prevHistory, ...nextKanban }));
    }

    if (Object.keys(nextIcons).length > 0) {
      setStatusIconsHistory((prevHistory) => ({ ...prevHistory, ...nextIcons }));
    }
  };

  const openUpdateBudgetModal = (carId: string) => {
    // If the user was dragging the kanban to scroll, ignore clicks on cards.
    if (Date.now() - kanbanDragRef.current.lastDragAt < 250) return;
    setSelectedCarId(carId);
    setCarHomeDetailsModalOpenOpen(true);
  };

  const handleKanbanPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const el = kanbanRef.current;
    if (!el) return;

    if (kanbanDragRef.current.animationId) {
      cancelAnimationFrame(kanbanDragRef.current.animationId);
      kanbanDragRef.current.animationId = null;
    }

    kanbanDragRef.current.isDown = true;
    kanbanDragRef.current.pointerId = e.pointerId;
    kanbanDragRef.current.startX = e.clientX;
    kanbanDragRef.current.lastX = e.clientX;
    kanbanDragRef.current.lastTime = performance.now();
    kanbanDragRef.current.velocity = 0;
    kanbanDragRef.current.startScrollLeft = el.scrollLeft;
    kanbanDragRef.current.didDrag = false;

    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      // no-op
    }

    setIsKanbanDragging(true);
  };

  const handleKanbanPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = kanbanRef.current;
    if (!el) return;
    if (!kanbanDragRef.current.isDown) return;

    const dx = e.clientX - kanbanDragRef.current.startX;
    if (Math.abs(dx) > 6) {
      kanbanDragRef.current.didDrag = true;
      kanbanDragRef.current.lastDragAt = Date.now();
    }

    const now = performance.now();
    const dt = now - kanbanDragRef.current.lastTime;
    if (dt > 0) {
      const deltaX = e.clientX - kanbanDragRef.current.lastX;
      kanbanDragRef.current.velocity = kanbanDragRef.current.velocity * 0.4 + (deltaX / dt) * 0.6;
    }
    kanbanDragRef.current.lastX = e.clientX;
    kanbanDragRef.current.lastTime = now;

    el.scrollLeft = kanbanDragRef.current.startScrollLeft - dx;
    e.preventDefault();
  };

  const endKanbanDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = kanbanRef.current;
    kanbanDragRef.current.isDown = false;
    if (el && kanbanDragRef.current.pointerId !== null) {
      try {
        el.releasePointerCapture(kanbanDragRef.current.pointerId);
      } catch {
        // no-op
      }
    }
    kanbanDragRef.current.pointerId = null;
    setIsKanbanDragging(false);

    const timeSinceLastMove = performance.now() - kanbanDragRef.current.lastTime;
    if (timeSinceLastMove < 100) {
      const velocity = kanbanDragRef.current.velocity;
      if (Math.abs(velocity) > 0.05) {
        let currentVelocity = -velocity * 16;
        const friction = 0.95;

        const step = () => {
          const container = kanbanRef.current;
          if (!container || Math.abs(currentVelocity) < 0.5) {
            kanbanDragRef.current.animationId = null;
            return;
          }

          container.scrollLeft += currentVelocity;
          currentVelocity *= friction;

          kanbanDragRef.current.animationId = requestAnimationFrame(step);
        };

        kanbanDragRef.current.animationId = requestAnimationFrame(step);
      }
    }
  };

  useEffect(() => {
    if (homeCarsQuery.error) {
      setError((homeCarsQuery.error as Error)?.message || 'Erro ao carregar carros');
      return;
    }

    setError(null);
  }, [homeCarsQuery.error]);

  useEffect(() => {
    const handler = async (e: Event) => {
      const { carId } = (e as CustomEvent<{ carId: string }>).detail;
      if (!carId) return;
      // Esteiras ainda não carregadas: gravar aqui apagaria a legenda do carro
      // com um array vazio (que nunca é recarregado). O reload por deps resolve.
      if (kanbanEsteiras.length === 0) return;

      try {
        const historyData = await getCarStatusHistory(carId);

        const approvedStatuses = historyData.history
          .filter((s: any) => s.approvalStatus === 'APPROVED')
          .sort((a: any, b: any) => new Date(b.changeDate).getTime() - new Date(a.changeDate).getTime());

        const approvedStatusIds = new Set(
          approvedStatuses.map((s: any) => s.esteiraId).filter(Boolean)
        );
        const approvedStatusChaves = new Set(
          approvedStatuses.map((s: any) => s.status).filter(Boolean)
        );

        const latestApproved = approvedStatuses.length > 0 ? approvedStatuses[0] : null;

        const newKanban = kanbanEsteiras.map((est) => ({
          name: est.chave,
          approved: est.id === (latestApproved as any)?.esteiraId || est.chave === (latestApproved as any)?.status,
        }));

        const newIcons = kanbanEsteiras.map((est) => ({
          id: est.id,
          name: est.chave,
          nome: est.nome,
          approved: approvedStatusIds.has(est.id) || approvedStatusChaves.has(est.chave),
        }));

        setKanbanStatusHistory((prev) => ({ ...prev, [carId]: newKanban }));
        setStatusIconsHistory((prev) => ({ ...prev, [carId]: newIcons }));
      } catch {
        // A mudança de esteira em si deu certo (veio por evento); só a recarga do
        // painel falhou — o kanban passa a mostrar dado velho até recarregar.
        // toastId fixo: o evento pode repetir muitas vezes e o toast não pode empilhar.
        notify.warning(
          'A ação foi concluída, mas a atualização da tela falhou. Recarregue a página.',
          { toastId: 'home-kanban-refresh' }
        );
      }
    };

    window.addEventListener('car:status-updated', handler);
    return () => window.removeEventListener('car:status-updated', handler);
  }, [kanbanEsteiras]);

  useEffect(() => {
    const carIds = cars.map((c: any) => c.id).filter(Boolean);

    if (carIds.length === 0) {
      setIsDetailsLoading(false);
      return;
    }

    let isMounted = true;

    const loadDetails = async () => {
      setIsDetailsLoading(true);

      try {
        // at-65: uma chamada para o lote inteiro (era 1 por veículo, vezes duas).
        await loadKanbanRegua(carIds);
      } finally {
        if (isMounted) {
          setIsDetailsLoading(false);
        }
      }
    };

    loadDetails();

    return () => {
      isMounted = false;
    };
    // Carrega atividades e histórico de esteiras dos carros do kanban quando a lista ou as
    // esteiras mudam. As funções auxiliares (loadCarsActivities, loadMissingCarsStatusHistory)
    // são recriadas a cada render e leem os caches de estado (ex.: kanbanStatusHistory) para
    // evitar re-buscar o que já foi carregado: incluí-las refaria as chamadas por atendimento
    // a cada preenchimento do cache (at-43).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cars, kanbanEsteiras]);

  useEffect(() => {
    const el = etapaTextoRef.current;
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
  }, []);

  useEffect(() => {
    // Guarda o objeto do ref numa variável local: ele nunca é substituído (apenas mutado),
    // então a limpeza cancela a animação do arrasto do kanban do mesmo objeto.
    const dragState = kanbanDragRef.current;

    return () => {
      if (dragState.animationId) {
        cancelAnimationFrame(dragState.animationId);
      }
    };
  }, []);

  const handleSearchButtonClick = () => {
    setAppliedSearchQuery(searchQuery);
  };

  // at-130: no Telão as abas ficam dentro do cabeçalho do quadro (que ocupa a tela toda), entre o logo e os indicadores.
  const abasDaHome = (
      <div className="recepcao-tabs" role="tablist" aria-label="Visão do painel">
        <button
          type="button"
          role="tab"
          aria-selected={aba === 'quadro'}
          className={`recepcao-tab ${aba === 'quadro' ? 'active' : ''}`}
          onClick={() => trocarAba('quadro')}
        >
          Quadro de esteiras
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={aba === 'telao'}
          className={`recepcao-tab ${aba === 'telao' ? 'active' : ''}`}
          onClick={() => trocarAba('telao')}
        >
          Telão
        </button>
      </div>
  );

  return (
    <div
      className="Frame1000004012"
      style={{
        height: loading ? '100vh' : 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: loading ? 'center' : 'space-between',
        alignItems: loading ? 'center' : 'flex-start',
        backgroundColor: loading ? '#808080' : 'transparent',
      }}
    >
      {loading && <FullScreenLoader />}

      {!loading && (
        <div className={aba === 'telao' ? 'ConteDo ConteDo--telao' : 'ConteDo ConteDo--quadro'}>
          {/* at-65: a faixa fica DENTRO do conteúdo; o `!error` apagava o painel inteiro. */}
          {error && (
            <div className="erro-de-tela" role="alert">
              {error}
            </div>
          )}
          {aba === 'quadro' && (
            <header className="home-topo">
              <div className="home-marca">
                <LogoSoac tamanho={44} corDoFundo="var(--tema-superficie, #ffffff)" />
                <button type="button" className="help-circle-btn" title="Como Funciona" onClick={() => setIsHelpOpen(true)}>
                  ?
                </button>
              </div>
              <div className="home-busca">
                <select
                  className="home-busca-filtro"
                  aria-label="Filtrar por"
                  title="Filtrar por"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                >
                  <option value="plate">Placa</option>
                  <option value="chassis">Chassi</option>
                  <option value="model">Modelo</option>
                  <option value="eventNumber">Número do Evento</option>
                </select>
                <div className="recepcao-search-bar home-busca-barra">
                  <button type="button" className="ItemSearch" onClick={handleSearchButtonClick} aria-label="Buscar">
                    <img className="imgIcon" src={SearchIcon} alt="" width="20" height="20" />
                  </button>
                  <input
                    type="text"
                    className="recepcao-search-input"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Digite o termo da pesquisa"
                    aria-label="Termo de busca"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSearchButtonClick();
                    }}
                  />
                </div>
              </div>
              <Relogio className="home-relogio" />
              <div className="home-barra">{abasDaHome}</div>
            </header>
          )}

          {aba === 'telao' ? (
            <TelaoOficina
              modo="aba"
              modalAberto={carHomeDetailsModalOpen || isHelpOpen}
              onAbrirVeiculo={openUpdateBudgetModal}
              onAbrirTelaCheia={() => {
                // Tela cheia do NAVEGADOR já no clique: o pedido só é aceito dentro do gesto do
                // usuário, e a tela cheia sobrevive à troca de rota (é o mesmo documento).
                document.documentElement.requestFullscreen?.().catch(() => undefined);
                navigate(TELAO());
              }}
              onAjuda={() => setIsHelpOpen(true)}
              abas={abasDaHome}
            />
          ) : (
          <div className="home-conteudo">
          <div className="Topo">
            <div className="HomeHero">
              <div className="HomeSummarySection HomeSummarySection--hero">
                <div className="HomeSummaryHeader">
                  <div className="HomeSummaryTitle">Resumo Operacional</div>
                  <div className="HomeSummarySubtitle">Visão consolidada do painel geral</div>
                </div>

                <div className="HomeSummaryGrid">
                  {summaryCards.map((card) => {
                    // at-46 (pedido de 02/08/2026): o card de veículos parados abre o modal
                    // com a lista completa — o número sozinho não dizia QUAIS veículos são.
                    const isParados = card.icon === 'warning';
                    return (
                      <div
                        key={card.label}
                        className={`HomeSummaryCard HomeSummaryCard--${card.variant} ${isParados ? 'HomeSummaryCard--clickable' : ''}`}
                        onClick={isParados ? () => { setParadosPage(1); setParadosModalOpen(true); } : undefined}
                        role={isParados ? 'button' : undefined}
                        title={isParados ? 'Ver a lista de veículos parados' : undefined}
                      >
                        <div className="HomeSummaryCardTop">
                          <div className={`HomeSummaryIcon HomeSummaryIcon--${card.variant}`}>
                            <SummaryIcon icon={card.icon} />
                          </div>
                          <div className="HomeSummaryCardLabel">{card.label}</div>
                        </div>
                        <div className="HomeSummaryCardValue">{card.value}</div>
                        <div className="HomeSummaryCardMeta">{card.meta}</div>
                      </div>
                    );
                  })}

                  <div className="HomeSummaryCard HomeSummaryCard--wide">
                    <div className="HomeSummaryCardHeaderRow">
                      <div className="HomeSummaryCardTop">
                        <div className="HomeSummaryIcon HomeSummaryIcon--wide">
                          <SummaryIcon icon="lanes" />
                        </div>
                        <div className="HomeSummaryCardLabel">Distribuição por esteira</div>
                      </div>
                      <div className="HomeSummaryBadge">{operationalSummary.laneTotal} em esteira</div>
                    </div>

                    <div className="HomeSummaryLanes">
                      {operationalSummary.laneItems.map((item) => (
                        <div
                          key={item.status}
                          className={`HomeSummaryLane HomeSummaryLane--${item.status}`}
                          title={item.label}
                          onClick={() => scrollToLane(item.status)}
                          style={{
                            cursor: 'pointer',
                            background: corDaEsteiraNoTema(item.cor, temaEscuro, 22).fundo,
                            border: `1px solid ${item.cor}`,
                          }}
                        >
                          <span className="HomeSummaryLaneName" style={{ color: corDaEsteiraNoTema(item.cor, temaEscuro, 22).texto }}>
                            {STATUS_LANE_SHORT_NAMES[item.status] || item.label}
                          </span>
                          <span className="HomeSummaryLaneValue" style={{ color: corDaEsteiraNoTema(item.cor, temaEscuro, 22).texto }}>{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* at-46: legenda das cores da régua de esteiras dos cards (pedido do usuário,
              02/08/2026) — o roxo do serviço inacabado é novo e as demais cores nunca
              tiveram explicação na tela. */}
          <div className="HomeLegendaCores">
            <span className="HomeLegendaCoresTitulo">Situação das esteiras:</span>
            <span className="HomeLegendaItem"><span className="HomeLegendaSwatch" style={{ background: 'rgb(255, 233, 191)', borderColor: 'rgb(255, 166, 0)' }} />Atual</span>
            <span className="HomeLegendaItem"><span className="HomeLegendaSwatch" style={{ background: 'var(--tema-tinta-verde, #E2FFDC)', borderColor: '#3BAB24' }} />Realizada</span>
            <span className="HomeLegendaItem"><span className="HomeLegendaSwatch" style={{ background: 'var(--tema-tinta-roxo, #ede9fe)', borderColor: '#8b5cf6' }} />Serviço inacabado</span>
            {/* at-99: a paralela é outro eixo — serviço acontecendo agora fora da etapa do
                veículo. Borda tracejada no swatch, igual à do chip, para a legenda e a régua
                dizerem a mesma coisa. */}
            <span className="HomeLegendaItem"><span className="HomeLegendaSwatch" style={{ background: 'var(--tema-tinta-verde, #ccfbf1)', borderColor: '#0d9488', borderStyle: 'dashed' }} />Paralela atual</span>
            <span className="HomeLegendaItem"><span className="HomeLegendaSwatch" style={{ background: 'rgb(255, 229, 229)', borderColor: 'rgb(221, 17, 17)' }} />Prevista</span>
            <span className="HomeLegendaItem"><span className="HomeLegendaSwatch" style={{ background: 'var(--tema-tinta-azul, #dbeafe)', borderColor: '#3b82f6' }} />Etapa do sistema</span>
            <span className="HomeLegendaItem"><span className="HomeLegendaSwatch HomeLegendaSwatch--apagado" style={{ background: 'var(--tema-fundo, #f3f4f6)', borderColor: 'var(--tema-linha-2, #d1d5db)' }} />Fora do planejamento</span>
          </div>

          <div className="HomeTTuloRecepcao">
            <div className="HomePainelGeralTitle">Painel Geral - {nonEntregaTotal} carros{foraDoTotal > 0 ? ` (${totalNaOficina} na oficina)` : ''}</div>
          </div>

          <div
            ref={kanbanRef}
            className={`HomeKanbanBoard${isKanbanDragging ? ' is-dragging' : ''}`}
            onPointerDown={handleKanbanPointerDown}
            onPointerMove={handleKanbanPointerMove}
            onPointerUp={endKanbanDrag}
            onPointerCancel={endKanbanDrag}
            onPointerLeave={endKanbanDrag}
          >
            {kanbanEsteiras.map((est) => {
              const laneCars = carsByLane[est.id] || [];
              const carsInThisStatus = laneCars.length;

              return (
                <div
                  key={est.id}
                  data-lane-id={est.id}
                  className={`Tabela-${est.chave} lane lane--${est.chave}`}
                  style={{
                    ['--lane-bg' as any]: corDaEsteiraNoTema(est.cor, temaEscuro, 12).fundo,
                    background: corDaEsteiraNoTema(est.cor, temaEscuro, 12).fundo,
                    ...(temaEscuro && est.cor ? { borderTop: `3px solid ${est.cor}` } : {}),
                  }}
                >
                  <div className="TTuloStatus lane__header">
                    <div
                      className={`Frame1000004025-${est.chave} lane__pill`}
                      style={{
                        background: temaEscuro
                          ? corDaEsteiraNoTema(est.cor, true, 30).fundo
                          : getContrastColor(est.cor) === 'var(--tema-superficie, #FFFFFF)' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.05)',
                        border: `1px solid ${temaEscuro && est.cor ? est.cor : getContrastColor(est.cor) === 'var(--tema-linha, #FFFFFF)' ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.15)'}`,
                      }}
                    >
                      <div className="TTuloStatusTexto" style={{ color: corDaEsteiraNoTema(est.cor, temaEscuro, 30).texto }}>{est.nome} - {carsInThisStatus} {carsInThisStatus === 1 ? 'carro' : 'carros'}</div>
                    </div>

                  </div>
                  <div className="TabelaBody lane__body">
                    {laneCars.map((car) => (
                      <div key={car.id} className="Frame1000004033 vehicleCardWrap" onClick={() => openUpdateBudgetModal(car.id)}>
                        <div className="CardPainelGeral vehicleCard">
                          <div className="TTuloPlaca">
                            <div className="Placa">{car.plate}</div>
                            <div className="Frame1000004065">
<div className={`ClasseDoCarroPreenchido badge ${car.carClass.replace(/\s/g, '')}`}>
                                <img src={CarWhite} alt="Galeria" />
                                <div className="CarClass">{car.carClass}</div>
                              </div>
                            </div>
                          </div>
                          <div className="CamposBoxCarro">
                            <div className="TTuloPlaca">
                              <div className="ModeloCor">{car.model} - {car.color}</div>
                              <div className="Frame1000004065">
                                {est.chave === 'AGUARDANDO' && (car.releaseStatus === 'TOTAL' || (!car.releaseStatus && car.releasedAt)) ? (
                                  <div className="ClasseDoCarroPreenchido badge" style={{ backgroundColor: '#137333' }}>
                                    <div className="WaitingReason" style={{ fontWeight: 600 }}>Liberado</div>
                                  </div>
                                ) : est.chave === 'AGUARDANDO' && car.releaseStatus === 'PARCIAL' ? (
                                  <div className="ClasseDoCarroPreenchido badge" style={{ backgroundColor: '#b45309' }}>
                                    <div className="WaitingReason" style={{ fontWeight: 600 }}>Liberação parcial</div>
                                  </div>
                                ) : car.returnedToWaiting ? (
                                  <div className="ClasseDoCarroPreenchido badge" style={{ backgroundColor: '#b45309' }}>
                                    <div className="WaitingReason" style={{ fontWeight: 600 }}>Retorno para ag.</div>
                                  </div>
                                ) : (
                                  <div className={`ClasseDoCarroPreenchido badge ${car.carClass.replace(/\s/g, '')}`}>
                                    <div className="WaitingReason">
                                      {(car.tags ?? []).filter((t: any) => t.valor).map((t: any) => {
                                        const opcao = (t.opcoes ?? []).find((o: any) => o.valor === t.valor);
                                        return opcao ? opcao.label : t.valor;
                                      }).join(' | ')}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="CamposBox">
                              <div className="CamposBoxTitulo">Dias para autorização:</div>
                              <div className="CamposBoxTexto">{car.daysToAuthorize !== null ? car.daysToAuthorize : 'N/A'}</div>
                            </div>
                            <div className="CamposBox">
                              <div className="CamposBoxTitulo">Dias em oficina externa:</div>
                              <div className="CamposBoxTexto">{car.externalShopDays !== null ? car.externalShopDays : 'N/A'}</div>
                            </div>
                            <div className="CamposBox">
                              <div className="CamposBoxTitulo">Dias em oficina:</div>
                              <div className="CamposBoxTexto">{car.daysInShop !== null ? car.daysInShop : 'N/A'}</div>
                            </div>
                            <div className="CamposBox">
                              <div className="CamposBoxTitulo">Previsão de entrega:</div>
                              <div className="CamposBoxTexto">{car.deliveryForecast !== null ? car.deliveryForecast : 'N/A'}</div>
                            </div>
                            <div className="CamposBox">
                              <div className="CamposBoxTitulo">Previsão de dias no setor:</div>
                              <div className="CamposBoxTexto">{car.estimatedDaysInStatus !== null ? car.estimatedDaysInStatus : 'N/A'}</div>
                            </div>
                          </div>
                          <div className="Frame1000004049">
                            <div className="CompraDePeAs vehicleCard__section">
                              <div className="CompraDePeAsTitulo">Esteiras</div>
                              <div className="Etapas vehicleCard__chips vehicleCard__chips--lanes">
                                {(() => {
                                  // at-130: a cascata de cores (e o porquê de cada passo) vive em
                                  // `regua.util.ts` — o Telão e o modal usam a mesma regra.
                                  const chips = derivarReguaDoVeiculo(
                                    car,
                                    statusIconsHistory[car.id] || [],
                                    plannedStatusesByCarId[car.id],
                                  );

                                  return chips.map((s) => {
                                    const statusColorClass = s.cor;
                                    const isDisabledByPlanning = s.foraDoPlanejamento;
                                    const isPlaceholder = s.oculto;

                                    return (
                                      <div key={s.id} className={`Etapa chip chip--${statusColorClass} ${statusColorClass} ${isDisabledByPlanning ? 'chip--disabled' : ''} ${isPlaceholder ? 'chip--placeholder' : ''}`} title={isPlaceholder ? undefined : s.nome}>
                                        <div
                                          ref={etapaTextoRef}
                                          className={`EtapaTexto ${isOverflowing ? 'pl-2' : 'text-center'}`}
                                          style={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: '50px',
                                          }}
                                        >
                                          {s.nome || s.name}
                                        </div>
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          </div>
          )}
        </div>
      )}
      <CarHomeDetailsModal
        isOpen={carHomeDetailsModalOpen}
        onCancel={() => setCarHomeDetailsModalOpenOpen(false)}
        carId={selectedCarId}
        sidebarRef={sidebarRef}
        isSidebarOpen={isStatusSidebarOpen}
      />

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona — Painel Geral"
        secoes={helpSections}
      />

      {/* at-46 (pedido de 02/08/2026): lista dos veículos parados (>48h sem movimentação),
          do parado há mais tempo para o mais recente, com paginação client-side — os carros
          já estão todos carregados na Home. */}
      {paradosModalOpen && (() => {
        const totalParados = operationalSummary.stoppedCars.length;
        const totalPages = Math.max(1, Math.ceil(totalParados / PARADOS_PAGE_SIZE));
        const page = Math.min(paradosPage, totalPages);
        const pageItems = operationalSummary.stoppedCars.slice(
          (page - 1) * PARADOS_PAGE_SIZE,
          page * PARADOS_PAGE_SIZE,
        );
        const diasParado = (ms: number) => {
          const dias = Math.floor(ms / (24 * 60 * 60 * 1000));
          return `${dias} dia${dias === 1 ? '' : 's'}`;
        };
        return (
          <div className="HomeParadosOverlay" onClick={() => setParadosModalOpen(false)}>
            <div className="HomeParadosModal" onClick={(e) => e.stopPropagation()}>
              <div className="HomeParadosHeader">
                <h3>Veículos parados — {totalParados}</h3>
                <button
                  type="button"
                  className="close-button"
                  onClick={() => setParadosModalOpen(false)}
                >
                  &times;
                </button>
              </div>
              <p className="HomeParadosSubtitle">
                Sem movimentação de esteira há mais de 48 horas, do parado há mais tempo para o
                mais recente. O tempo conta desde a última mudança de esteira do veículo.
              </p>
              <div className="HomeParadosLista">
                <div className="HomeParadosRow HomeParadosRow--head">
                  <span>Placa</span>
                  <span>Modelo</span>
                  <span>Esteira atual</span>
                  <span>Parado há</span>
                </div>
                {pageItems.map(({ car, stoppedMs }) => (
                  <div key={car.id} className="HomeParadosRow">
                    <span
                      className="HomeParadosPlaca"
                      onClick={() => {
                        setParadosModalOpen(false);
                        setSelectedCarId(car.id);
                        setCarHomeDetailsModalOpenOpen(true);
                      }}
                      title="Ver detalhes do veículo"
                    >
                      {car.plate || '-'}
                    </span>
                    <span>{car.model || '-'}</span>
                    <span>{car.statusLabel || labelDe(car.status) || '-'}</span>
                    <span className="HomeParadosTempo">{diasParado(stoppedMs)}</span>
                  </div>
                ))}
              </div>
              {totalPages > 1 && (
                <div className="HomeParadosPaginacao">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setParadosPage(page - 1)}
                  >
                    Anterior
                  </button>
                  <span>Página {page} de {totalPages}</span>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setParadosPage(page + 1)}
                  >
                    Próxima
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default HomePage;
